/**
 * features/finance/attachmentWrite.ts
 * ------------------------------------------------------------------
 * Putting evidence on a finance record, and taking it off again.
 *
 * THE BYTES GO FIRST, THE RECORD SECOND. A record pointing at a file that
 * failed to upload is a paperclip icon promising evidence that is not
 * there — worse than no attachment, because somebody checking a voucher
 * sees the icon and stops looking. The other order fails safely: an
 * orphaned file in the bucket costs storage and misleads nobody.
 *
 * A FAILED UPLOAD CLEANS UP AFTER ITSELF. If the Firestore write fails
 * after the bytes landed, the bytes are removed — otherwise every retry
 * leaves another copy of the same invoice in the bucket.
 *
 * REMOVING IS SUPERSEDING. The bytes go, the record stays and says who
 * removed them and why. See lib/attachments for the argument.
 * ------------------------------------------------------------------ */
import { collection, doc } from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { db, storage, setDoc, updateDoc } from '@/lib/firebase';
import {
  ATTACHMENT_COLLECTION, attachmentProblem, storagePathFor,
  type AttachmentKind, type EvidenceType, type FinanceAttachment,
} from '@/lib/attachments';

export interface UploadInput {
  kind: AttachmentKind;
  recordId: string;
  recordRef?: string;
  evidence: EvidenceType;
  note?: string;
  file: File;
  actor: { uid: string; name?: string };
}

export async function uploadAttachment(input: UploadInput): Promise<FinanceAttachment> {
  const problem = attachmentProblem(input.file);
  if (problem) throw new Error(problem);

  const at = new Date();
  const path = storagePathFor(input.kind, input.recordId, input.file.name, at);
  const objectRef = ref(storage, path);

  await uploadBytes(objectRef, input.file, { contentType: input.file.type });

  let downloadUrl: string;
  try {
    downloadUrl = await getDownloadURL(objectRef);
  } catch (e) {
    await deleteObject(objectRef).catch(() => {});
    throw e;
  }

  const docRef = doc(collection(db, ATTACHMENT_COLLECTION));
  const record: FinanceAttachment = {
    id: docRef.id,
    kind: input.kind,
    recordId: input.recordId,
    ...(input.recordRef ? { recordRef: input.recordRef } : {}),
    evidence: input.evidence,
    /* The name the person recognises, not the flattened one. Only the
       path is sanitised — a file called "Kgosi's invoice.pdf" should
       still read that way in the list. */
    fileName: input.file.name,
    storagePath: path,
    downloadUrl,
    contentType: input.file.type,
    sizeBytes: input.file.size,
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
    uploadedBy: input.actor.uid,
    ...(input.actor.name ? { uploadedByName: input.actor.name } : {}),
    uploadedAt: at.toISOString(),
  };

  try {
    await setDoc(docRef, record);
  } catch (e) {
    /* The bytes landed and the record did not. Left alone, every retry
       adds another copy of the same invoice to the bucket. */
    await deleteObject(objectRef).catch(() => {});
    throw e;
  }

  return record;
}

/**
 * Remove the file, keep the record.
 *
 * A reason is required. "Removed" with no explanation, on the evidence
 * behind a payment, is exactly the entry somebody would want explained a
 * year later — and nobody remembers by then.
 */
export async function supersedeAttachment(
  a: FinanceAttachment,
  reason: string,
  actor: { uid: string; name?: string },
): Promise<void> {
  if (!reason.trim()) {
    throw new Error('Say why the file is being removed — this is the evidence behind a payment.');
  }
  if (a.supersededAt) throw new Error('That file has already been removed.');

  if (a.storagePath) {
    await deleteObject(ref(storage, a.storagePath)).catch(() => {
      /* Already gone from the bucket. The record still needs marking, and
         refusing here would leave it claiming a file that is not there. */
    });
  }

  await updateDoc(doc(db, ATTACHMENT_COLLECTION, a.id), {
    supersededAt: new Date().toISOString(),
    supersededBy: actor.uid,
    supersededByName: actor.name ?? '',
    supersededReason: reason.trim(),
    storagePath: null,
    downloadUrl: null,
  });
}
